// node modules
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Query } from '@apollo/client/react/components';
import { gql } from '@apollo/client';

// gql
import BookPreview from 'components/ContentView/BookPreview.gql';

// app modules
import RelatedContent from 'components/RelatedContent';

// Common fields used in RelatedRecipes, RelatedReferences and BackLinks queries.
const RELATED_CONTENT_PROJECTION = `
  id
  authors {
    id
    first_name
    last_name
    is_author
  }
  book {
    ...BookPreview
    isbn
    title
    image_name
    image_permission
    authors {
      id
      first_name
      last_name
      is_author
    }
  }
  chapter
  title
  subtitle
  instagram_thumbnail_url
  primary_image {
    credit
    crops(filter: ["square"]) {
      sizes {
        src
        w
        h
      }
      aspect_ratio
      type
    }
    graphictype
    id
    height
    src
    type
    width
  }
`;

class RelatedContentQuery extends PureComponent {
  static propTypes = {
    dualColumn: PropTypes.bool,
    forceSsr: PropTypes.bool,
    lazy: PropTypes.bool,
    onLoad: PropTypes.func,
    query: PropTypes.oneOf(['recipes', 'references', 'backlinks']).isRequired,
    recipeId: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['Recipe', 'Encyclopedia', 'Section']).isRequired,
  };

  static defaultProps = {
    dualColumn: false,
    forceSsr: false,
    lazy: false,
    onLoad: () => {},
  };

  static QUERIES = {
    recipes: gql`
      query GetRelatedRecipes($id: String, $type: String, $limit: Int, $includeTags: [String]) {
        RelatedRecipes(id: $id, type: $type, limit: $limit, includeTags: $includeTags) {
          ${RELATED_CONTENT_PROJECTION}
          _explanation
        }
      }
      ${BookPreview}
    `,
    references: gql`
      query GetRelatedReferences($id: String, $type: String, $limit: Int, $includeTags: [String]) {
        RelatedReferences(id: $id, type: $type, limit: $limit, includeTags: $includeTags) {
          ${RELATED_CONTENT_PROJECTION}
          _explanation
        }
      }
       ${BookPreview}
    `,
    backlinks: gql`
      query GetBackLinks($id: String, $type: String) {
        BackLinks(id: $id, type: $type) {
          ${RELATED_CONTENT_PROJECTION}
        }
      }
      ${BookPreview}
    `,
  };

  static skeletons = count =>
    new Array(count).fill().map((_, i) => ({
      id: `skeleton-${i}`,
      skeleton: true,
    }));

  constructor(props) {
    super(props);
    this.rootRef = React.createRef();
    this.state = {
      visible: false,
    };
  }

  componentDidMount() {
    this.observer = new IntersectionObserver(this.onIntersectionEvent, {
      rootMargin: '100px',
    });
    this.observer.observe(this.rootRef.current);
  }

  componentDidUpdate(prevProps) {
    const { visible } = this.state;

    if (prevProps.recipeId !== this.props.recipeId) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ visible: false });
    }

    this.observer.disconnect();
    if (!visible) {
      this.observer.observe(this.rootRef.current);
    }
  }

  componentWillUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  onIntersectionEvent = event => {
    if (event[0].isIntersecting) {
      this.setState({ visible: true });
    }
  };

  render() {
    const { recipeId, query, type, dualColumn, forceSsr, lazy, onLoad } = this.props;
    const { visible } = this.state;

    let skip = lazy && !visible;
    if (forceSsr) {
      skip = false;
    }

    return (
      <div ref={this.rootRef}>
        <Query
          query={RelatedContentQuery.QUERIES[query]}
          variables={{
            id: recipeId,
            limit: 10,
            type,
          }}
          ssr={forceSsr}
          skip={skip}
          onCompleted={onLoad}
        >
          {({ loading, error, data }) => {
            if (error) {
              return <div>{error.message}</div>;
            }

            switch (query) {
              case 'recipes':
                return (
                  <RelatedContent
                    dualColumn={dualColumn}
                    items={data?.RelatedRecipes || RelatedContentQuery.skeletons(4)}
                    heading="Related Recipes"
                    loading={loading}
                  />
                );
              case 'references':
                return (
                  <RelatedContent
                    dualColumn={dualColumn}
                    items={data?.RelatedReferences || RelatedContentQuery.skeletons(4)}
                    heading="Related Reference"
                    loading={loading}
                  />
                );
              case 'backlinks':
                return (
                  <RelatedContent
                    dualColumn={dualColumn}
                    items={data?.BackLinks}
                    heading="Also Mentioned In"
                  />
                );
              default:
                return null;
            }
          }}
        </Query>
      </div>
    );
  }
}

export default RelatedContentQuery;
